import {
  ApplicationInsights,
  type ICustomProperties,
  type ITelemetryItem,
} from "@microsoft/applicationinsights-web";

// Nothing here may be written to the device: no cookie, no local or session storage. Privacy policy
// §8.2 promises exactly that, and it is why the portal needs no consent banner.
//
// Deliberately not shared with the brochure's copy. The brochure keeps ?ref= and utm_* for
// attribution; the portal must destroy everything but filter state, because its sign-in redirects
// carry authorisation codes.

// The strategy list and My strategies hold their filter, sort and paging state in the query
// string. An allow-list because forgetting to allow a parameter loses a dimension, while forgetting
// to block one publishes a secret.
const KEPT_QUERY_PARAMS = new Set([
  "symbol",
  "assetClass",
  "riskProfile",
  "direction",
  "timeframe",
  "minAvgAnnualProfit",
  "minWinRate",
  "maxDrawdown",
  "name",
  "sort",
  "cycle",
  "page",
  "status",
]);

const MAX_PARAM_LENGTH = 100;

/** The URL with every non-allow-listed query parameter and the fragment removed. */
export function scrubUrl(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl, window.location.origin);
  } catch {
    // Losing a row is cheaper than guessing at a URL that might hold a token.
    return "";
  }

  const kept = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (KEPT_QUERY_PARAMS.has(key)) kept.append(key, value.slice(0, MAX_PARAM_LENGTH));
  }

  url.search = kept.toString();
  // MSAL receives the authorisation code in the fragment, not the query.
  url.hash = "";
  return url.toString();
}

// Dependency names arrive as "GET https://host/path?query" — the whole URL, not just the path.
const METHOD_AND_URL = /^([A-Z]+) (.+)$/;

function scrubUrls(item: ITelemetryItem) {
  // The operation name is the page's location, fragment included, and becomes ai.operation.name
  // on every item — so a sign-in response on the landing URL would ride along on all of them.
  const trace = item.ext?.trace;
  if (typeof trace?.name === "string") trace.name = trace.name.split(/[?#]/)[0];

  const data = item.baseData;
  if (!data) return;

  for (const field of ["uri", "refUri", "target"]) {
    if (typeof data[field] === "string") data[field] = scrubUrl(data[field]);
  }

  if (typeof data.name === "string") {
    const match = METHOD_AND_URL.exec(data.name);
    if (match) data.name = `${match[1]} ${scrubUrl(match[2])}`;
  }

  // Some SDK features report a URL as an ordinary property, where the fields above cannot see it.
  const properties = data.properties as Record<string, unknown> | undefined;
  if (!properties) return;

  for (const [key, value] of Object.entries(properties)) {
    if (/(url|uri)$/i.test(key) && typeof value === "string") {
      properties[key] = scrubUrl(value);
    }
  }
}

// The SDK records browser and OS but never screen or window size. Numbers land in
// customMeasurements and strings in customDimensions.
function stampClientContext(item: ITelemetryItem) {
  item.data = item.data ?? {};

  item.data.viewportWidth = window.innerWidth;
  item.data.viewportHeight = window.innerHeight;
  item.data.screenWidth = window.screen?.width;
  item.data.devicePixelRatio = window.devicePixelRatio;
  item.data.orientation = window.screen?.orientation?.type ?? "unknown";

  try {
    item.data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    item.data.timezone = "unknown";
  }
}

let lastPageViewUri: string | null = null;

// Route tracking reports some navigations twice, such as the return from sign-in, so a page view for
// the URL just reported is the same visit rather than a new one.
function isRepeatPageView(item: ITelemetryItem) {
  if (item.baseType !== "PageviewData") return false;

  const uri = typeof item.baseData?.uri === "string" ? item.baseData.uri : null;
  const repeat = uri !== null && uri === lastPageViewUri;
  lastPageViewUri = uri;
  return repeat;
}

function telemetryInitializer(item: ITelemetryItem) {
  scrubUrls(item);
  // Compared after scrubbing, so URLs differing only in a dropped parameter count as one view.
  if (isRepeatPageView(item)) return false;
  stampClientContext(item);
  return true;
}

// Only the portal API gets trace headers. Sending them to Entra or Stripe would fail their CORS
// preflights. An empty list sends them nowhere, same-origin included.
function correlationHeaderDomains(): string[] {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) return [];

  try {
    return [new URL(apiUrl).hostname];
  } catch {
    return [];
  }
}

let appInsights: ApplicationInsights | null = null;

/** Starts telemetry, or stays silent when the build carries no connection string. */
export function initTelemetry() {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
  if (appInsights || !connectionString) return;

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      disableCookiesUsage: true,
      isStorageUseDisabled: true,
      // Defaults to sessionStorage, which would pass "sets no cookies" while still writing to the
      // device.
      enableSessionStorageBuffer: false,
      // React Router navigates through the History API, so without this there is one page view
      // per tab. These page views carry no duration; time on a page is the gap to the next page
      // view in the same session. autoTrackPageVisitTime is deliberately not set: it writes to
      // sessionStorage and reports the previous page's raw URL as a property.
      enableAutoRouteTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      enableCorsCorrelation: true,
      correlationHeaderDomains: correlationHeaderDomains(),
    },
  });

  appInsights.loadAppInsights();
  // Before the first page view, or the landing URL — the one carrying the sign-in response — goes
  // out unscrubbed.
  appInsights.addTelemetryInitializer(telemetryInitializer);
  appInsights.trackPageView();
}

export function trackEvent(name: string, properties: ICustomProperties) {
  appInsights?.trackEvent({ name }, properties);
}

export function trackException(error: unknown, properties?: ICustomProperties) {
  const exception = error instanceof Error ? error : new Error(String(error));
  appInsights?.trackException({ exception }, properties);
}
